import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiCheck, FiX, FiSend } from 'react-icons/fi';

const Leave = () => {
  const { success, error: showError } = useToast();
  const [types, setTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const [leaveWorkflowId, setLeaveWorkflowId] = useState(null);
  const [leaveApprovalRequests, setLeaveApprovalRequests] = useState([]);

  const [showAddType, setShowAddType] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: '', isPaid: true, accrualDaysPerYear: '28' });

  const [showAddRequest, setShowAddRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({ employeeId: '', leaveTypeId: '', startDate: '', endDate: '', reason: '' });

  const loadAll = () => {
    setLoading(true);
    const params = statusFilter ? { status: statusFilter } : {};
    Promise.all([
      apiClient.get('/hr/leave/types'),
      apiClient.get('/hr/leave/requests', { params }),
      apiClient.get('/hr/employees'),
      apiClient.get('/workflow/workflows', { params: { appliesToType: 'leave_request' } }).catch(() => ({ data: { data: [] } })),
      apiClient.get('/workflow/approvals').catch(() => ({ data: { data: [] } })),
    ]).then(([typesRes, requestsRes, employeesRes, wfRes, reqRes]) => {
      setTypes(typesRes.data.data);
      setRequests(requestsRes.data.data);
      setEmployees(employeesRes.data.data);
      const activeLeaveWorkflow = wfRes.data.data.find((w) => w.isActive);
      setLeaveWorkflowId(activeLeaveWorkflow ? activeLeaveWorkflow.id : null);
      setLeaveApprovalRequests(reqRes.data.data.filter((r) => r.entityType === 'leave_request'));
    }).catch((err) => showError(getErrorMessage(err, 'Could not load leave data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const employeeName = (id) => {
    const emp = employees.find((e) => e.id === id);
    return emp ? `${emp.firstName} ${emp.lastName}` : id;
  };

  const typeName = (id) => types.find((t) => t.id === id)?.name || id;
  const approvalForLeaveRequest = (requestId) => leaveApprovalRequests.find((r) => r.entityId === requestId);

  const handleAddType = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/hr/leave/types', typeForm);
      success('✅ Leave type added');
      setShowAddType(false);
      setTypeForm({ name: '', isPaid: true, accrualDaysPerYear: '28' });
      loadAll();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not add leave type'));
    }
  };

  const handleAddRequest = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...requestForm };
      if (!payload.reason) delete payload.reason;
      await apiClient.post('/hr/leave/requests/admin', payload);
      success('✅ Leave request logged');
      setShowAddRequest(false);
      setRequestForm({ employeeId: '', leaveTypeId: '', startDate: '', endDate: '', reason: '' });
      loadAll();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not log leave request'));
    }
  };

  const handleDecide = async (requestId, decision) => {
    try {
      await apiClient.patch(`/hr/leave/requests/${requestId}/decision`, { decision });
      success(`✅ Request ${decision}`);
      loadAll();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not update request'));
    }
  };

  const handleSubmitLeaveForApproval = async (request) => {
    if (!leaveWorkflowId) return;
    try {
      await apiClient.post('/workflow/approvals', {
        workflowId: leaveWorkflowId,
        entityType: 'leave_request',
        entityId: request.id,
      });
      success('✅ Submitted for approval');
      loadAll();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not submit for approval'));
    }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Leave</h1>
        <p>Leave types and time-off requests</p>
      </div>

      <section className="page-section">
        <div className="section-header">
          <h2>Leave Types</h2>
          <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowAddType((s) => !s)}>
            <FiPlus size={14} /> {showAddType ? 'Cancel' : 'Add Type'}
          </button>
        </div>
        {showAddType && (
          <form onSubmit={handleAddType} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>Name *</label>
              <input required value={typeForm.name} onChange={(e) => setTypeForm(p => ({ ...p, name: e.target.value }))} placeholder="Annual Leave" />
            </div>
            <div className="form-field">
              <label>Accrual Days / Year</label>
              <input type="number" value={typeForm.accrualDaysPerYear} onChange={(e) => setTypeForm(p => ({ ...p, accrualDaysPerYear: e.target.value }))} />
            </div>
            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                <input type="checkbox" checked={typeForm.isPaid} onChange={(e) => setTypeForm(p => ({ ...p, isPaid: e.target.checked }))} style={{ width: 'auto' }} />
                Paid
              </label>
            </div>
            <div className="form-field full-width form-actions">
              <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save</button>
            </div>
          </form>
        )}
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Paid</th><th>Accrual / Year</th></tr></thead>
            <tbody>
              {types.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>No leave types yet</td></tr>
              ) : types.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td>{t.isPaid ? 'Yes' : 'No'}</td>
                  <td>{t.accrualDaysPerYear ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="page-section">
        <div className="section-header">
          <h2>Requests</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 'auto', padding: '0.5rem' }}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowAddRequest((s) => !s)}>
              <FiPlus size={14} /> {showAddRequest ? 'Cancel' : 'Log Request'}
            </button>
          </div>
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1rem' }}>
          "Log Request" is for HR logging a request on an employee's behalf (phoned or emailed in) — this is separate from self-service, which needs the employee's own login.
        </p>
        {showAddRequest && (
          <form onSubmit={handleAddRequest} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>Employee *</label>
              <select required value={requestForm.employeeId} onChange={(e) => setRequestForm(p => ({ ...p, employeeId: e.target.value }))}>
                <option value="">-- select --</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Leave Type *</label>
              <select required value={requestForm.leaveTypeId} onChange={(e) => setRequestForm(p => ({ ...p, leaveTypeId: e.target.value }))}>
                <option value="">-- select --</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="form-field"><label>Start Date *</label><input required type="date" value={requestForm.startDate} onChange={(e) => setRequestForm(p => ({ ...p, startDate: e.target.value }))} /></div>
            <div className="form-field"><label>End Date *</label><input required type="date" value={requestForm.endDate} onChange={(e) => setRequestForm(p => ({ ...p, endDate: e.target.value }))} /></div>
            <div className="form-field full-width"><label>Reason</label><input value={requestForm.reason} onChange={(e) => setRequestForm(p => ({ ...p, reason: e.target.value }))} /></div>
            <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Log Request</button></div>
          </form>
        )}
        {!leaveWorkflowId && (
          <p style={{ color: '#6b7280', fontSize: '0.8rem', marginBottom: '1rem' }}>
            No active approval workflow is set up for leave requests yet — requests can only be approved directly. Set one up under Workflow → Workflows (Applies To: Leave Request) to enable routed approval.
          </p>
        )}
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Employee</th><th>Type</th><th>Dates</th><th>Days</th><th>Reason</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No requests found</td></tr>
              ) : requests.map((r) => {
                const approval = approvalForLeaveRequest(r.id);
                return (
                  <tr key={r.id}>
                    <td>{employeeName(r.employeeId)}</td>
                    <td>{typeName(r.leaveTypeId)}</td>
                    <td>{new Date(r.startDate).toLocaleDateString()} – {new Date(r.endDate).toLocaleDateString()}</td>
                    <td>{r.totalDays}</td>
                    <td>{r.reason || '—'}</td>
                    <td>
                      <span className="badge" style={{ background: r.status === 'approved' ? '#10b981' : r.status === 'rejected' ? '#ef4444' : r.status === 'cancelled' ? '#9ca3af' : '#f59e0b', color: 'white' }}>
                        {r.status}
                      </span>
                    </td>
                    <td>
                      <div className="actions" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                        {approval && (
                          <span className="badge" style={{
                            background: approval.status === 'approved' ? '#10b981' : approval.status === 'rejected' ? '#ef4444' : approval.status === 'escalated' ? '#f59e0b' : '#3b82f6',
                            color: 'white',
                          }}>
                            Approval: {approval.status}
                          </span>
                        )}
                        {r.status === 'pending' && !approval && leaveWorkflowId && (
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSubmitLeaveForApproval(r)}>
                            <FiSend size={14} /> Submit for Approval
                          </button>
                        )}
                        {r.status === 'pending' && (
                          <div className="actions">
                            <button className="action-icon edit" title="Approve" onClick={() => handleDecide(r.id, 'approved')}>
                              <FiCheck size={16} />
                            </button>
                            <button className="action-icon delete" title="Reject" onClick={() => handleDecide(r.id, 'rejected')}>
                              <FiX size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </motion.div>
  );
};

export default Leave;